import type { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import type { SiteSectionId } from "@/data/common/ids";
import type { SectionNode } from "@/domains/v4b/site-section/site-section.types";
import type { IVirtualizedTreeNode } from "@voltalis/ui-kit/components";

export const PENDING_SITE_SECTION_PREVIEW_KEY = "__pending-site-section-preview__";

export type TPendingSiteSectionPreviewType = "creation" | "move";

type TreeNode = IVirtualizedTreeNode<TSiteSectionsTreeData>;

export type TPendingSiteSectionPreviewNode = TreeNode & {
  key: typeof PENDING_SITE_SECTION_PREVIEW_KEY;
  isPendingSiteSectionPreview: true;
  pendingPreviewType: TPendingSiteSectionPreviewType;
  data: SectionNode;
  state: NonNullable<TreeNode["state"]> & {
    isPendingSiteSectionPreview: true;
    pendingSiteSectionPreviewType: TPendingSiteSectionPreviewType;
  };
};

export type TPendingSiteSectionPreview =
  | {
      type: "creation";
      name: string;
      parent: TSiteSectionsTreeData;
      sourceTree?: TreeNode;
    }
  | {
      type: "move";
      name: string;
      section: SectionNode;
      parent: TSiteSectionsTreeData;
      sourceTree?: TreeNode;
    };

function getChildren(node: TreeNode): TreeNode[] {
  return node.children ?? [];
}

function cloneNode(node: TreeNode, children = getChildren(node).map((child) => cloneNode(child))): TreeNode {
  return {
    ...node,
    state: node.state ? { ...node.state } : undefined,
    children,
  };
}

function removePendingPreviewNodes(node: TreeNode): TreeNode {
  return cloneNode(
    node,
    getChildren(node)
      .filter((child) => !isPendingSiteSectionPreviewNode(child))
      .map((child) => removePendingPreviewNodes(child)),
  );
}

function removeSectionNode(nodes: TreeNode[], sectionId: SiteSectionId): TreeNode[] {
  return nodes.flatMap((node) => {
    if (node.data?.id === sectionId) return [];

    return [cloneNode(node, removeSectionNode(getChildren(node), sectionId))];
  });
}

function findPathToSection(node: TreeNode, sectionId: SiteSectionId, path: TreeNode[] = []): TreeNode[] | undefined {
  for (const child of getChildren(node)) {
    const nextPath = [...path, child];

    if (child.data?.id === sectionId) {
      return nextPath;
    }

    const childPath = findPathToSection(child, sectionId, nextPath);
    if (childPath) return childPath;
  }
}

function createPendingPreviewNode(
  type: TPendingSiteSectionPreviewType,
  name: string,
  parent: TSiteSectionsTreeData,
): TPendingSiteSectionPreviewNode {
  return {
    key: PENDING_SITE_SECTION_PREVIEW_KEY,
    name,
    isPendingSiteSectionPreview: true,
    pendingPreviewType: type,
    data: {
      id: PENDING_SITE_SECTION_PREVIEW_KEY as unknown as SiteSectionId,
      name,
      parent: parent
        ? {
            id: parent.id,
            namePath: parent.parent ? `${parent.parent.namePath}/${parent.name}` : parent.name,
          }
        : undefined,
      applianceCountByCategory: {},
      children: [],
    },
    state: {
      isExpanded: true,
      isPendingSiteSectionPreview: true,
      pendingSiteSectionPreviewType: type,
    },
    children: [],
  };
}

function appendPreviewToParent(node: TreeNode, parentId: SiteSectionId, previewNode: TreeNode): [TreeNode, boolean] {
  if (node.data?.id === parentId) {
    return [cloneNode(node, [previewNode, ...getChildren(node).map((child) => cloneNode(child))]), true];
  }

  let wasInserted = false;
  const children = getChildren(node).map((child) => {
    if (wasInserted) return cloneNode(child);

    const [nextChild, didInsert] = appendPreviewToParent(child, parentId, previewNode);
    wasInserted = didInsert;
    return nextChild;
  });

  return [cloneNode(node, children), wasInserted];
}

function clonePathNode(node: TreeNode, children: TreeNode[]): TreeNode {
  return cloneNode(node, children);
}

function mergePreviewPath(children: TreeNode[], path: TreeNode[], previewNode: TreeNode): TreeNode[] {
  const [pathHead, ...pathTail] = path;

  if (!pathHead) return children;

  const existingIndex = children.findIndex((child) => child.data?.id === pathHead.data?.id);

  if (existingIndex === -1) {
    const pathChildren = pathTail.length ? mergePreviewPath([], pathTail, previewNode) : [previewNode];
    return [...children.map((child) => cloneNode(child)), clonePathNode(pathHead, pathChildren)];
  }

  return children.map((child, index) => {
    if (index !== existingIndex) return cloneNode(child);

    const mergedChildren = pathTail.length
      ? mergePreviewPath(getChildren(child), pathTail, previewNode)
      : [previewNode, ...getChildren(child).map((currentChild) => cloneNode(currentChild))];

    return cloneNode(child, mergedChildren);
  });
}

export function isPendingSiteSectionPreviewNode(
  node: IVirtualizedTreeNode<TSiteSectionsTreeData> | undefined,
): node is TPendingSiteSectionPreviewNode {
  return Boolean(
    node &&
    "isPendingSiteSectionPreview" in node &&
    (node as { isPendingSiteSectionPreview?: unknown }).isPendingSiteSectionPreview === true,
  );
}

export function applyPendingSiteSectionPreview(
  siteSectionsTree: TreeNode | undefined,
  preview: TPendingSiteSectionPreview | null,
): TreeNode | undefined {
  if (!siteSectionsTree) return;

  let tree = removePendingPreviewNodes(siteSectionsTree);

  if (!preview) return tree;

  const sourceTree = removePendingPreviewNodes(preview.sourceTree ?? siteSectionsTree);

  if (preview.type === "move") {
    tree = cloneNode(tree, removeSectionNode(getChildren(tree), preview.section.id));
  }

  const pendingPreviewNode = createPendingPreviewNode(preview.type, preview.name, preview.parent);

  if (!preview.parent) {
    return cloneNode(tree, [pendingPreviewNode, ...getChildren(tree).map((child) => cloneNode(child))]);
  }

  const [nextTree, wasInserted] = appendPreviewToParent(tree, preview.parent.id, pendingPreviewNode);

  if (wasInserted) return nextTree;

  const sourcePath = findPathToSection(sourceTree, preview.parent.id);
  if (!sourcePath) return tree;

  return cloneNode(tree, mergePreviewPath(getChildren(tree), sourcePath, pendingPreviewNode));
}
